import React, { useMemo, useState } from 'react';
import { Search, Download, Eye, Trash2, XCircle } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const STATUSES = [
  { id: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-800' },
  { id: 'paid', label: 'Paid', color: 'bg-blue-100 text-blue-800' },
  { id: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-800' },
  { id: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

export default function BookingsTab() {
  const { bookings, pujas, pandits, refreshBookings } = useData();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);

  const updateStatus = async (id, s) => {
    try {
      await api.put(`/api/bookings/${id}/status`, { status: s });
      await refreshBookings();
      toast({ title: `Booking marked ${s}` });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await api.delete(`/api/bookings/${id}`);
      await refreshBookings();
      toast({ title: 'Booking deleted' });
    } catch (e) { toast({ title: formatApiError(e), variant: 'destructive' }); }
  };

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      const ms = status === 'all' || b.status === status;
      const query = q.trim().toLowerCase();
      const mq = !query
        || b.user?.name?.toLowerCase().includes(query)
        || b.user?.phone?.toLowerCase().includes(query)
        || b.user?.email?.toLowerCase().includes(query)
        || b.pujaTitle?.toLowerCase().includes(query);
      return ms && mq;
    });
  }, [bookings, q, status]);

  const exportCSV = () => {
    const rows = [['ID', 'Date', 'Name', 'Phone', 'Email', 'Puja', 'Package', 'Amount', 'Status']];
    filtered.forEach((b) => {
      rows.push([b.id, new Date(b.createdAt || b.id).toLocaleString(), b.user?.name, b.user?.phone, b.user?.email, b.pujaTitle, b.package, b.amount, b.status]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bookings-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / phone / email / puja..." className="w-full pl-9 pr-3 py-2.5 border border-orange-200 rounded-lg text-sm bg-white" />
        </div>
        <select data-testid="booking-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 border border-orange-200 rounded-lg text-sm bg-white">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
        </select>
        <button data-testid="export-bookings-btn" onClick={exportCSV} className="px-4 py-2.5 rounded-lg border border-orange-300 text-orange-700 text-sm font-semibold hover:bg-orange-50 flex items-center gap-1">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-orange-100 rounded-2xl p-10 text-center text-stone-500">No bookings match your filters.</div>
      ) : (
        <div className="bg-white border border-orange-100 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="bookings-table">
            <thead className="bg-orange-50">
              <tr>
                <th className="text-left px-3 py-3">Name</th>
                <th className="text-left px-3 py-3">Puja</th>
                <th className="text-left px-3 py-3">Package</th>
                <th className="text-left px-3 py-3">Pandit</th>
                <th className="text-left px-3 py-3">Phone</th>
                <th className="text-left px-3 py-3">Amount</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const p = pujas.find((x) => x.slug === b.pujaSlug);
                const pd = p && pandits.find((x) => x.id === p.panditId);
                const st = STATUSES.find((s) => s.id === b.status) || STATUSES[0];
                return (
                  <tr key={b.id} className="border-t border-orange-100 hover:bg-orange-50/40">
                    <td className="px-3 py-3"><div className="font-medium">{b.user?.name}</div><div className="text-[10px] text-stone-500">{b.user?.email}</div></td>
                    <td className="px-3 py-3 max-w-[200px]"><div className="line-clamp-2">{b.pujaTitle}</div></td>
                    <td className="px-3 py-3 text-xs">{b.package || '-'}</td>
                    <td className="px-3 py-3 text-xs">{pd ? pd.name_en : <span className="text-red-500">Unassigned</span>}</td>
                    <td className="px-3 py-3">{b.user?.phone}</td>
                    <td className="px-3 py-3 font-semibold text-orange-700">₹ {b.amount}</td>
                    <td className="px-3 py-3">
                      <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} className={`text-xs rounded-full px-2 py-1 border-0 ${st.color} font-semibold cursor-pointer`}>
                        {STATUSES.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setSelected(b)} title="View" className="p-1.5 rounded-md hover:bg-orange-100 text-orange-700"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => del(b.id)} title="Delete" className="p-1.5 rounded-md hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-bold text-nv-wine">Booking Details</h3>
              <button onClick={() => setSelected(null)} className="p-1 hover:bg-stone-100 rounded"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <p><b>Booking ID:</b> {selected.id}</p>
              <p><b>Puja:</b> {selected.pujaTitle}</p>
              <p><b>Package:</b> {selected.package}</p>
              <p><b>Devotee:</b> {selected.user?.name}</p>
              <p><b>Gotra:</b> {selected.user?.gotra}</p>
              <p><b>Phone:</b> {selected.user?.phone}</p>
              <p><b>Email:</b> {selected.user?.email}</p>
              <p><b>Address:</b> {selected.user?.address}</p>
              <p><b>Amount:</b> ₹ {selected.amount}</p>
              <p><b>Status:</b> {selected.status}</p>
              <p><b>Booked at:</b> {new Date(selected.createdAt || selected.id).toLocaleString()}</p>
              {selected.samagri?.length > 0 && (
                <div>
                  <b>Samagri:</b>
                  <ul className="list-disc list-inside">
                    {selected.samagri.map((s, i) => (<li key={i}>{s.name} - ₹{s.price}</li>))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
